/**
 * Shipment tracking service using Pipedream proxy to Shippo API.
 * Authenticates with the Salesforce access token.
 */

const TRACKING_API_URL = 'https://eoqf8gxqcbby7hr.m.pipedream.net/'

const MAX_SHIPMENT_AGE_DAYS = 90

/**
 * Extracts the carrier name from a carrierServiceLevel string.
 * e.g. "UPS Ground" → "UPS", "FedEx Home Delivery" → "FedEx"
 */
export function carrierFromServiceLevel(carrierServiceLevel: string): string {
  return carrierServiceLevel.split(' ')[0] || carrierServiceLevel
}

export interface TrackingRequest {
  carrier: string
  orderNumber: string
  trackingNumber: string
}

export interface TrackingLocation {
  city: string
  state: string
  zip: string
  country: string
}

export interface TrackingSubstatus {
  code: string
  text: string
  action_required: boolean
}

export interface TrackingStatusInfo {
  status: string
  status_date: string
  status_details: string
  location: TrackingLocation
  substatus: TrackingSubstatus
}

export interface TrackingHistoryEntry {
  status: string
  status_date: string
  status_details: string
  location: TrackingLocation
  substatus: TrackingSubstatus
}

export interface TrackingResponse {
  tracking_number: string
  carrier: string
  servicelevel: {
    name: string | null
    token: string | null
  }
  address_from: TrackingLocation | null
  address_to: TrackingLocation | null
  eta: string | null
  original_eta: string | null
  test: boolean
  tracking_status: TrackingStatusInfo | null
  tracking_history: TrackingHistoryEntry[]
  messages: string[]
}

export type TrackingResult =
  | { status: 'available'; data: TrackingResponse }
  | { status: 'no_info'; carrier: string; trackingUrl?: string }
  | { status: 'too_old'; carrier: string; trackingUrl?: string }
  | { status: 'error'; message: string }

/**
 * Returns true if the shipment is too old to bother fetching tracking info.
 */
export function isShipmentTooOld(shipTime: string | undefined): boolean {
  if (!shipTime) return false
  const shipDate = new Date(shipTime)
  const ageInDays = (Date.now() - shipDate.getTime()) / (1000 * 60 * 60 * 24)
  return ageInDays > MAX_SHIPMENT_AGE_DAYS
}

/**
 * Returns true if the API response contains actual tracking data.
 */
function hasTrackingInfo(response: TrackingResponse): boolean {
  return (
    response.tracking_status !== null || response.tracking_history.length > 0
  )
}

/**
 * Fetch real-time tracking info for a shipment.
 *
 * @param request Shipment identifiers including tracking number and carrier.
 * @param accessToken Salesforce access token used as Bearer auth.
 * @param shipTime Ship date from Salesforce, used to skip old shipments.
 * @param trackingUrl Existing tracking URL for fallback link.
 */
export async function fetchTracking(
  request: TrackingRequest,
  accessToken: string,
  shipTime?: string,
  trackingUrl?: string
): Promise<TrackingResult> {
  if (isShipmentTooOld(shipTime)) {
    return {
      status: 'too_old',
      carrier: request.carrier,
      trackingUrl
    }
  }

  try {
    const resp = await fetch(TRACKING_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(request)
    })

    const text = await resp.text()

    if (!resp.ok) {
      return {
        status: 'error',
        message: `HTTP ${resp.status}: ${text.slice(0, 200)}`
      }
    }

    let data: TrackingResponse
    try {
      data = JSON.parse(text)
    } catch {
      return {
        status: 'error',
        message: `Invalid response: ${text.slice(0, 200)}`
      }
    }

    if (!hasTrackingInfo(data)) {
      return {
        status: 'no_info',
        carrier: data.carrier || request.carrier,
        trackingUrl
      }
    }

    return { status: 'available', data }
  } catch (e: unknown) {
    return {
      status: 'error',
      message: e instanceof Error ? e.message : 'Network request failed'
    }
  }
}
